<template>
  <div>
    <p class="text-primary ntpl-intro">
      Personaliza los mensajes que se envían automáticamente a tus clientes. Si
      no configuras una plantilla, se usará la plantilla por defecto de Mydessk.
    </p>

    <div v-if="templates.length" class="ntpl-layout">
      <!-- ── Selector lateral ── -->
      <div class="ntpl-selector">
        <template v-for="group in groupedTemplates">
          <p class="ntpl-group-title" :key="`g-${group.label}`">
            {{ group.label }}
          </p>
          <button
            v-for="tpl in group.items"
            :key="tpl.type"
            type="button"
            class="ntpl-item"
            :class="{ 'ntpl-item--active': activeType === tpl.type }"
            @click="activeType = tpl.type"
          >
            <span class="ntpl-item__name">{{ tpl.label }}</span>
            <span v-if="tpl.has_custom" class="ntpl-item__badge"
              >Personalizada</span
            >
          </button>
        </template>
      </div>

      <!-- ── Panel de edición ── -->
      <div class="ntpl-panel" v-if="activeTemplate">
        <!-- Header del panel -->
        <div class="ntpl-panel__header">
          <div>
            <h6 class="ntpl-panel__title">{{ activeTemplate.label }}</h6>
            <span
              v-if="activeTemplate.has_custom"
              class="ntpl-panel__custom-badge"
            >
              <i class="el-icon-check"></i> Plantilla personalizada activa
            </span>
            <span v-else class="ntpl-panel__default-badge">
              Usando plantilla por defecto de Mydessk
            </span>
          </div>
          <a
            v-if="activeTemplate.has_custom"
            href="#"
            @click.prevent="resetTemplate(activeTemplate.type)"
            class="ntpl-panel__reset"
          >
            <i class="el-icon-refresh-left"></i> Restaurar defecto
          </a>
        </div>

        <el-form
          :ref="`form-${activeTemplate.type}`"
          :model="forms[activeTemplate.type]"
          label-position="top"
          class="ntpl-form"
        >
          <!-- Variables -->
          <div class="ntpl-variables">
            <p class="ntpl-variables__title">Variables disponibles</p>
            <div class="ntpl-variables__list">
              <span
                v-for="variable in activeTemplate.variables"
                :key="variable"
                class="ntpl-variable"
                @click="insertVariable(variable)"
                title="Click para copiar"
                >{{ variable }}</span
              >
            </div>
            <small class="ntpl-variables__hint">
              Click en una variable para copiarla al portapapeles.
            </small>
          </div>

          <!-- Asunto -->
          <el-form-item prop="email_subject" class="ntpl-field">
            <label class="ntpl-field__label">Asunto del email</label>
            <el-input
              v-model="forms[activeTemplate.type].email_subject"
              placeholder="Asunto del correo electrónico"
            />
          </el-form-item>

          <!-- Cuerpo email -->
          <el-form-item prop="email_body" class="ntpl-field">
            <label class="ntpl-field__label">Cuerpo del email (HTML)</label>
            <EditorWysiwygComponent
              @onEditorChange="
                ({ text }) => (forms[activeTemplate.type].email_body = text)
              "
              :setText.sync="forms[activeTemplate.type].email_body"
            />
          </el-form-item>

          <!-- WhatsApp -->
          <el-form-item prop="whatsapp_body" class="ntpl-field">
            <label class="ntpl-field__label"
              >Mensaje de WhatsApp
              <span class="ntpl-field__optional">(texto plano)</span></label
            >
            <el-input
              v-model="forms[activeTemplate.type].whatsapp_body"
              type="textarea"
              :rows="4"
              :maxlength="1000"
              show-word-limit
              placeholder="Mismo mensaje en texto plano para WhatsApp. Máx. 1000 caracteres."
            />
          </el-form-item>

          <!-- Acciones -->
          <div class="ntpl-actions">
            <ButtonSaveComponent
              @onSaveClick="handleSave(activeTemplate.type)"
            />
            <transition name="fade">
              <span v-if="saved[activeTemplate.type]" class="ntpl-saved">
                <i class="el-icon-check"></i> Guardado correctamente
              </span>
            </transition>
          </div>
        </el-form>
      </div>
    </div>

    <!-- Estado vacío -->
    <div v-else class="ntpl-empty">
      <i class="el-icon-loading"></i> Cargando plantillas...
    </div>
  </div>
</template>

<script>
import configService from "../../services/configService";
import ButtonSaveComponent from "../../../components/ButtonSave";
import EditorWysiwygComponent from "../../../components/EditorWysiwyg";

const GROUPS = [
  {
    label: "Facturas",
    types: [
      "invoice_before_due",
      "invoice_after_due",
      "invoice_created",
      "invoice_cash"
    ]
  },
  {
    label: "Cotizaciones",
    types: ["quote_followup", "installment_due"]
  },
  {
    label: "Contratos",
    types: ["contract_sign_invite"]
  }
];

export default {
  name: "NotificationTemplatesComponent",
  components: { ButtonSaveComponent, EditorWysiwygComponent },
  data: () => ({
    activeType: "invoice_before_due",
    templates: [],
    forms: {},
    saved: {}
  }),
  computed: {
    activeTemplate() {
      return this.templates.find(t => t.type === this.activeType) || null;
    },
    groupedTemplates() {
      return GROUPS.map(group => ({
        label: group.label,
        items: group.types
          .map(type => this.templates.find(t => t.type === type))
          .filter(Boolean)
      })).filter(g => g.items.length);
    }
  },
  methods: {
    async load() {
      this.$store.dispatch("toggleRequestLoading");
      try {
        const res = await configService
          .getNotificationTemplates()
          .finally(() => this.$store.dispatch("toggleRequestLoading"));

        if (res.data.success) {
          this.templates = res.data.data;
          const forms = {};
          this.templates.forEach(tpl => {
            forms[tpl.type] = {
              email_subject: tpl.template?.email_subject || "",
              email_body: tpl.template?.email_body || "",
              whatsapp_body: tpl.template?.whatsapp_body || ""
            };
          });
          this.forms = forms;
        }
      } catch (e) {
        return false;
      }
    },

    async handleSave(type) {
      this.$store.dispatch("toggleRequestLoading");
      try {
        const res = await configService
          .upsertNotificationTemplate(type, this.forms[type])
          .finally(() => this.$store.dispatch("toggleRequestLoading"));

        if (res.data.success) {
          this.$notifications.success({ message: res.data.message });
          this.$set(this.saved, type, true);
          setTimeout(() => this.$set(this.saved, type, false), 3000);
          await this.load();
        }
      } catch (e) {
        return false;
      }
    },

    async resetTemplate(type) {
      try {
        await this.$confirm(
          "¿Eliminar tu plantilla personalizada y volver a usar la de Mydessk?",
          "Restaurar plantilla",
          {
            confirmButtonText: "Restaurar",
            cancelButtonText: "Cancelar",
            type: "warning"
          }
        );
        this.$store.dispatch("toggleRequestLoading");
        const res = await configService
          .deleteNotificationTemplate(type)
          .finally(() => this.$store.dispatch("toggleRequestLoading"));

        if (res.data.success) {
          this.$notifications.success({ message: res.data.message });
          await this.load();
        }
      } catch (e) {
        return false;
      }
    },

    insertVariable(variable) {
      const textarea = document.createElement("textarea");
      textarea.value = variable;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        this.$message({
          type: "success",
          message: `${variable} copiado.`,
          duration: 1500
        });
      } catch (e) {
        this.$message({ type: "info", message: variable });
      }
      document.body.removeChild(textarea);
    }
  },
  mounted() {
    this.load();
  }
};
</script>

<style scoped lang="scss">
$primary: #31355c;
$secondary: #3ecfb4;
$border: #e4eaf6;
$bg: #f8f9fc;
$muted: #909399;
$white: #fff;
$green: #1d9e75;

.ntpl-intro {
  font-size: 14px;
  margin-bottom: 24px;
  line-height: 1.6;
}

// ── Layout dos columnas ──
.ntpl-layout {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

// ── Selector lateral ──
.ntpl-selector {
  border: 1px solid $border;
  border-radius: 10px;
  overflow: hidden;
  background: $white;
}

.ntpl-group-title {
  font-size: 10px;
  font-weight: 700;
  color: $muted;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin: 0;
  padding: 10px 14px 6px;
  background: $bg;
  border-bottom: 1px solid $border;
}

.ntpl-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  padding: 10px 14px;
  background: $white;
  border: none;
  border-bottom: 1px solid $border;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: $bg;
  }

  &--active {
    background: lighten($secondary, 35%);
    border-left: 3px solid $secondary;

    .ntpl-item__name {
      color: $primary;
      font-weight: 700;
    }
  }
}

.ntpl-item__name {
  font-size: 13px;
  color: $primary;
  line-height: 1.4;
}

.ntpl-item__badge {
  font-size: 10px;
  background: $green;
  color: $white;
  border-radius: 10px;
  padding: 1px 7px;
  font-weight: 600;
}

// ── Panel de edición ──
.ntpl-panel {
  border: 1px solid $border;
  border-radius: 10px;
  background: $white;
  overflow: hidden;
}

.ntpl-panel__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid $border;
  background: $bg;
  gap: 12px;
}

.ntpl-panel__title {
  font-size: 15px;
  font-weight: 700;
  color: $primary;
  margin: 0 0 4px;
}

.ntpl-panel__custom-badge {
  font-size: 12px;
  color: $green;
  font-weight: 600;
}

.ntpl-panel__default-badge {
  font-size: 12px;
  color: $muted;
}

.ntpl-panel__reset {
  font-size: 12px;
  color: #f56c6c;
  white-space: nowrap;
  text-decoration: none;
  flex-shrink: 0;

  &:hover {
    text-decoration: underline;
  }
}

// ── Formulario ──
.ntpl-form {
  padding: 20px;
}

// ── Variables ──
.ntpl-variables {
  background: $bg;
  border: 1px solid $border;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
}

.ntpl-variables__title {
  font-size: 13px;
  font-weight: 600;
  color: $primary;
  margin: 0 0 8px;
}

.ntpl-variables__list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}

.ntpl-variable {
  cursor: pointer;
  font-size: 12px;
  padding: 3px 9px;
  background: #e8f0fe;
  color: $primary;
  border-radius: 4px;
  font-family: monospace;
  transition: all 0.12s;

  &:hover {
    background: $primary;
    color: $white;
  }
}

.ntpl-variables__hint {
  font-size: 11px;
  color: $muted;
}

// ── Campos ──
.ntpl-field {
  margin-bottom: 20px !important;
}

.ntpl-field__label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: $primary;
  margin-bottom: 6px;
  line-height: 1;
}

.ntpl-field__optional {
  font-size: 12px;
  color: $muted;
  font-weight: 400;
  margin-left: 4px;
}

// ── Acciones ──
.ntpl-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
}

.ntpl-saved {
  font-size: 13px;
  color: $green;
  font-weight: 600;
}

// ── Vacío ──
.ntpl-empty {
  text-align: center;
  padding: 40px;
  color: $muted;
  font-size: 14px;
}

// ── Texto de inputs igual al cuerpo del editor ──
::v-deep .ntpl-form {
  .el-input__inner,
  .el-textarea__inner {
    color: $primary;
    font-size: 14px;
    font-weight: 400;
    font-family: inherit;
    line-height: 1.6;
  }

  // El textarea de WhatsApp visualmente igual al ql-editor
  .el-textarea__inner {
    padding: 12px 14px;
    letter-spacing: 0;
  }
}

// ── Transición ──
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
